// src/utils/money.utils.js

/**
 * Money class for precise currency operations
 * Uses built-in JavaScript Decimal handling with careful precision management
 */
export class Money {
  constructor(amount, currency = 'EUR') {
    // Store amount as string to maintain precision
    this.amount = typeof amount === 'string' ? amount : amount.toString();
    this.currency = currency;
  }

  /**
   * Create a Money object from a decimal value
   */
  static fromDecimal(amount, currency = 'EUR') {
    return new Money(amount.toString(), currency);
  }

  /**
   * Add another Money object
   */
  add(other) {
    if (this.currency !== other.currency) {
      throw new Error(`Cannot add Money with different currencies: ${this.currency} and ${other.currency}`);
    }

    // Use JavaScript's Number for calculation but maintain precision by converting to/from string
    const sum = (parseFloat(this.amount) + parseFloat(other.amount)).toFixed(20);
    // Trim trailing zeros
    const trimmed = sum.replace(/\.?0+$/, '');
    return new Money(trimmed, this.currency);
  }

  /**
   * Subtract another Money object
   */
  subtract(other) {
    if (this.currency !== other.currency) {
      throw new Error(`Cannot subtract Money with different currencies: ${this.currency} and ${other.currency}`);
    }

    const difference = (parseFloat(this.amount) - parseFloat(other.amount)).toFixed(20);
    // Trim trailing zeros
    const trimmed = difference.replace(/\.?0+$/, '');
    return new Money(trimmed, this.currency);
  }

  /**
   * Multiply by a number (e.g., for quantity calculations)
   */
  multiply(multiplier) {
    const product = (parseFloat(this.amount) * parseFloat(multiplier.toString())).toFixed(20);
    // Trim trailing zeros
    const trimmed = product.replace(/\.?0+$/, '');
    return new Money(trimmed, this.currency);
  }

  /**
   * Apply a percentage discount
   */
  applyPercentageDiscount(percentage) {
    const multiplier = (1 - (parseFloat(percentage) / 100)).toString();
    return this.multiply(multiplier);
  }

  /**
   * Apply an absolute discount
   */
  applyAbsoluteDiscount(discount) {
    const discountMoney = new Money(discount.toString(), this.currency);
    return this.subtract(discountMoney);
  }

  /**
   * Convert to another currency using an exchange rate
   */
  convertTo(targetCurrency, exchangeRate) {
    const converted = (parseFloat(this.amount) * parseFloat(exchangeRate)).toFixed(20);
    // Trim trailing zeros
    const trimmed = converted.replace(/\.?0+$/, '');
    return new Money(trimmed, targetCurrency);
  }

  /**
   * Format for display according to locale and currency
   */
  format(locale = 'en-US') {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: this.currency
      }).format(parseFloat(this.amount));
    } catch (error) {
      console.error('Error formatting money:', error);
      // Fallback formatting
      return `${this.currency} ${parseFloat(this.amount).toFixed(2)}`;
    }
  }

  /**
   * Format with parts for custom styling
   */
  formatToParts(locale = 'en-US') {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: getCurrencyDecimalPlaces(this.currency),
        maximumFractionDigits: getCurrencyDecimalPlaces(this.currency)
      }).formatToParts(parseFloat(this.amount));
    } catch (error) {
      console.error('Error formatting money to parts:', error);
      // Return a simplified parts array as fallback
      const amount = parseFloat(this.amount).toFixed(getCurrencyDecimalPlaces(this.currency));
      const [integer, fraction = ''] = amount.split('.');
      return [
        { type: 'integer', value: integer },
        { type: 'decimal', value: '.' },
        { type: 'fraction', value: fraction }
      ];
    }
  }

  /**
   * Round to the appropriate number of decimal places for the currency
   */
  round() {
    const decimalPlaces = getCurrencyDecimalPlaces(this.currency);
    const rounded = parseFloat(this.amount).toFixed(decimalPlaces);
    return new Money(rounded, this.currency);
  }

  /**
   * Get the numeric value (careful with precision - only use for display)
   */
  toNumber() {
    return parseFloat(this.amount);
  }

  /**
   * Get the string representation (for storage)
   */
  toString() {
    return this.amount;
  }
}

/**
 * Get the appropriate number of decimal places for a currency
 */
export function getCurrencyDecimalPlaces(currencyCode) {
  // Direct import to avoid circular dependencies
  try {
    // Use dynamic import (might not work in all environments)
    const { currencies } = require('./currencies.utils');
    return currencies[currencyCode]?.decimalPlaces ?? 2;
  } catch (error) {
    // Fallback for common currencies if import fails
    console.warn(error);
    const decimalPlaces = {
      EUR: 2,
      USD: 2,
      GBP: 2,
      JPY: 0,
      BTC: 8,
      ETH: 18
    };
    return decimalPlaces[currencyCode] || 2;
  }
}

/**
 * Creates a pricing structure with prices in multiple currencies
 */
export function createMultiCurrencyPricing(baseAmount, baseCurrency, targetCurrencies, exchangeRates, discount = null) {
  // Create a base Money object
  const baseMoney = new Money(baseAmount, baseCurrency);

  // Initialize pricing structure
  const pricing = {
    original: { [baseCurrency]: baseMoney.round().toString() },
    current: {},
    baseCurrency,
    discount: discount || { type: 'none', value: 0 }
  };

  // Calculate current price after discount
  let currentBaseMoney = baseMoney;
  if (discount) {
    if (discount.type === 'percentage') {
      currentBaseMoney = baseMoney.applyPercentageDiscount(discount.value);
    } else if (discount.type === 'absolute') {
      currentBaseMoney = baseMoney.applyAbsoluteDiscount(discount.value);
    }
  }

  // Store the rounded current price in base currency
  pricing.current[baseCurrency] = currentBaseMoney.round().toString();

  // Calculate prices for all target currencies
  targetCurrencies.forEach(currency => {
    if (currency === baseCurrency) return;

    // Get exchange rate for this currency
    const rate = exchangeRates[currency];
    if (!rate) return;

    try {
      // Convert and round prices
      const originalInTarget = baseMoney.convertTo(currency, rate).round();
      const currentInTarget = currentBaseMoney.convertTo(currency, rate).round();

      // Store as strings to maintain precision
      pricing.original[currency] = originalInTarget.toString();
      pricing.current[currency] = currentInTarget.toString();
    } catch (error) {
      console.error(`Error converting price to ${currency}:`, error);
    }
  });

  return pricing;
}

/**
 * Displays a money amount in a currency-aware way
 */
export function formatMoney(amount, currency, locale = 'en-US') {
  return new Money(amount, currency).format(locale);
}

/**
 * Convert a pricing object to Money objects
 */
export function getPricingAsMoney(pricing, preferredCurrency) {
  const currency = pricing.current[preferredCurrency] ? preferredCurrency : pricing.baseCurrency;

  return {
    original: new Money(pricing.original[currency], currency),
    current: new Money(pricing.current[currency], currency),
    currency
  };
}
